/**
 * "Ece senin için önerdi" — the rail of recommendations from other household members.
 *
 * Grouped by sender rather than shown as one mixed row, because the sender is the reason
 * the card is interesting. A single row titled "Önerilenler" would read as more output
 * from the recommender, which is exactly what these are not.
 *
 * Kept out of `rows.tsx` because a card here carries things no other card does: who sent
 * it, what they said, an unread state, and a way to dismiss it. Generalising `ItemCard` to
 * cover that would push four props into every grid tile in the app to serve one screen.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProfileRecommendationDto } from '@filmodasi/shared';
import { api } from '../api/client';
import { builtInAvatarSrc } from './Avatar';
import { MediaImage } from './MediaImage';
import { Rail } from './rows';
import { detailPath } from '../catalog/detailPath';

function SenderBadge({ item }: { item: ProfileRecommendationDto }) {
  if (item.senderAvatarId) {
    return <img className="fromhh__badge" src={builtInAvatarSrc(item.senderAvatarId)} alt="" />;
  }
  return (
    <span className="fromhh__badge" style={{ background: item.senderAvatarColor }}>
      {[...item.senderName][0]?.toLocaleUpperCase('tr') ?? '?'}
    </span>
  );
}

function RecommendationCard({
  entry,
  onChanged,
}: {
  entry: ProfileRecommendationDto;
  onChanged: () => void;
}) {
  /*
   * Hidden locally the moment it is dismissed, before the reload lands.
   *
   * The alternative — wait for the refetch — leaves the card sitting there for as long as
   * the round trip takes, which reads as the button not working and invites a second
   * click on something already gone.
   */
  const [gone, setGone] = useState(false);
  if (gone) return null;

  const art = entry.item.poster ?? entry.item.logo;

  const markRead = () => {
    if (entry.readAt) return;
    // Fire-and-forget: this is a courtesy flag, and blocking navigation to the film on it
    // would make the whole row feel slower for no benefit.
    void api(`/api/profile-recommendations/${entry.id}/read`, { method: 'PATCH' }).then(
      onChanged,
      () => {},
    );
  };

  const dismiss = () => {
    setGone(true);
    void api(`/api/profile-recommendations/${entry.id}`, { method: 'DELETE' }).then(
      onChanged,
      // Put it back if the server refused — a card that vanishes and then reappears on the
      // next load is more confusing than one that never left.
      () => setGone(false),
    );
  };

  return (
    <div className={`card card--poster fromhh${entry.readAt ? '' : ' fromhh--unread'}`}>
      {/*
        Through `detailPath`, and this was a bug rather than a preference.

        The link was hard-coded to `/watch/:id`. `RecommendButton` sits on the series detail
        screen and recommends the *series*, so an inbox card for one pointed the player at
        an item that has no stream behind it — a show is a container for episodes. It also
        skipped the detail screen for films, which is where somebody would go to find out
        what they have been sent.
      */}
      <Link to={detailPath(entry.item)} onClick={markRead} title={entry.item.title}>
        <div className="card__art">
          <MediaImage
            src={art}
            shape="poster"
            fallback={entry.item.title}
            seed={entry.item.id}
          />
          <SenderBadge item={entry} />
        </div>
        <span className="card__title">{entry.item.title}</span>
      </Link>
      {entry.message && <p className="fromhh__note">“{entry.message}”</p>}
      <button type="button" className="link fromhh__dismiss" onClick={dismiss}>
        Kaldır
      </button>
    </div>
  );
}

export function FromHouseholdRail({
  items,
  onChanged,
}: {
  items: ProfileRecommendationDto[];
  onChanged: () => void;
}) {
  if (items.length === 0) return null;

  // One rail per sender, newest sender first. `items` already arrives newest-first, so
  // insertion order into the Map is the order the rails should appear in.
  const bySender = new Map<string, ProfileRecommendationDto[]>();
  for (const item of items) {
    const list = bySender.get(item.senderName);
    if (list) list.push(item);
    else bySender.set(item.senderName, [item]);
  }

  return (
    <>
      {[...bySender].map(([sender, entries]) => (
        <Rail key={sender} title={`${sender} senin için önerdi`}>
          {entries.map((entry) => (
            <RecommendationCard key={entry.id} entry={entry} onChanged={onChanged} />
          ))}
        </Rail>
      ))}
    </>
  );
}
